import throttle from 'lodash.throttle';

/**
 * Tracks viewport scroll direction and toggles state classes on a target element.
 *
 * Stabilization is intentionally built in for sticky-layout scenarios where tiny
 * layout-driven scroll oscillations can cause false direction flips.
 */
export class DeltaScroll {
  /**
   * @param {HTMLElement} element The target element.
   */
  constructor(element) {
    this.element = element;

    this._scrollingDownClass = 'is-scrolling-down';
    this._scrollingUpClass = 'is-scrolling-up';

    // Ignore tiny scroll movement that is typically layout noise.
    this._directionDeltaThreshold = 2;

    // Short lock after direction switch to suppress immediate synthetic reversal.
    this._directionLockMs = 220;

    // Opposite-direction travel required before accepting a real reversal.
    this._oppositeDirectionDistanceThreshold = 14;

    // Neutral zone around the buffer where direction classes are cleared.
    this._bufferStabilizationThreshold = 16;

    this._currentDirection = null;
    this._directionLockUntil = 0;
    this._pendingDirection = null;
    this._pendingDirectionDistance = 0;

    this.buffer = this._parseBuffer(element.dataset.deltaScroll);
    this.lastScrollY = window.scrollY;
    this.onScroll = throttle(this._handleScroll.bind(this), 100);

    this.element.DeltaScroll = this;
    this._bindEvents();
  }

  _bindEvents() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  _parseBuffer(value) {
    const parsedValue = parseInt(value, 10);

    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      return 0;
    }

    return parsedValue;
  }

  /**
   * Scroll event handler that resolves direction with hysteresis.
   * @returns {void}
   */
  _handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.lastScrollY;
    const scrollDeltaAbs = Math.abs(scrollDelta);
    const direction = scrollDelta > 0 ? 'down' : 'up';

    if (scrollDeltaAbs < this._directionDeltaThreshold) {
      this.lastScrollY = currentScrollY;
      return;
    }

    if (this._isOppositeDirectionLocked(direction)) {
      this.lastScrollY = currentScrollY;
      return;
    }

    if (!this._canApplyDirection(direction, scrollDeltaAbs)) {
      this.lastScrollY = currentScrollY;
      return;
    }

    if (direction === 'down') {
      this._applyScrollingDown(currentScrollY);
    } else if (direction === 'up') {
      this._applyScrollingUp(currentScrollY);
    }

    this.lastScrollY = currentScrollY;
  }

  /**
   * Prevents immediate opposite-direction flips during the lock window.
   * @param {'down'|'up'} direction Candidate direction.
   * @returns {boolean}
   */
  _isOppositeDirectionLocked(direction) {
    if (!this._currentDirection || this._currentDirection === direction) {
      return false;
    }

    return Date.now() < this._directionLockUntil;
  }

  /**
   * Confirms a direction reversal only after enough opposite travel distance.
   * @param {'down'|'up'} direction Candidate direction.
   * @param {number} scrollDeltaAbs Absolute scroll delta.
   * @returns {boolean}
   */
  _canApplyDirection(direction, scrollDeltaAbs) {
    if (!this._currentDirection || this._currentDirection === direction) {
      this._resetPendingDirection();
      return true;
    }

    if (this._pendingDirection !== direction) {
      this._pendingDirection = direction;
      this._pendingDirectionDistance = scrollDeltaAbs;
      return false;
    }

    this._pendingDirectionDistance += scrollDeltaAbs;

    if (this._pendingDirectionDistance >= this._oppositeDirectionDistanceThreshold) {
      this._resetPendingDirection();
      return true;
    }

    return false;
  }

  /**
   * Clears pending reversal state.
   * @returns {void}
   */
  _resetPendingDirection() {
    this._pendingDirection = null;
    this._pendingDirectionDistance = 0;
  }

  /**
   * Stores the active direction and starts the lock window.
   * @param {'down'|'up'} direction Resolved direction.
   * @returns {void}
   */
  _setDirection(direction) {
    if (this._currentDirection === direction) {
      return;
    }

    this._currentDirection = direction;
    this._directionLockUntil = Date.now() + this._directionLockMs;
  }

  /**
   * Indicates whether scroll position is near the configured top buffer.
   * @param {number} currentScrollY Current viewport Y offset.
   * @returns {boolean}
   */
  _isInBufferStabilizationZone(currentScrollY) {
    return currentScrollY <= this.buffer + this._bufferStabilizationThreshold;
  }

  /**
   * Resets direction tracking and removes direction classes.
   * @returns {void}
   */
  _resetDirectionState() {
    this.element.classList.remove(this._scrollingDownClass);
    this.element.classList.remove(this._scrollingUpClass);
    this._currentDirection = null;
    this._directionLockUntil = 0;
    this._resetPendingDirection();
  }

  /**
   * Applies downward state unless within the top stabilization zone.
   * @param {number} currentScrollY Current viewport Y offset.
   * @returns {void}
   */
  _applyScrollingDown(currentScrollY) {
    if (this._isInBufferStabilizationZone(currentScrollY)) {
      this._resetDirectionState();
      return;
    }

    this._setDirection('down');

    this.element.classList.add(this._scrollingDownClass);
    this.element.classList.remove(this._scrollingUpClass);
  }

  /**
   * Applies upward state unless within the top stabilization zone.
   * @param {number} currentScrollY Current viewport Y offset.
   * @returns {void}
   */
  _applyScrollingUp(currentScrollY) {
    if (this._isInBufferStabilizationZone(currentScrollY)) {
      this._resetDirectionState();
      return;
    }

    this._setDirection('up');

    this.element.classList.add(this._scrollingUpClass);
    this.element.classList.remove(this._scrollingDownClass);
  }
}
